import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Anchor } from 'lucide-react';
import { Button } from '../components/ui/button';

export const PolitiqueConfidentialite = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Button>
            <div className="flex items-center gap-2">
              <Anchor className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-lg">The Myli Boat</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Politique de Confidentialité</h1>

        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                La présente Politique de Confidentialité décrit comment <strong>The Myli Boat</strong> 
                (ZEMMOUR Salim, Entrepreneur individuel, SIRET : 850 462 664 00036) collecte, utilise, 
                partage et protège les données personnelles des utilisateurs du site 
                <a href="https://www.themyliboat.com" className="text-blue-600 hover:underline"> www.themyliboat.com</a>.
              </p>
              <p>
                Cette politique est conforme au Règlement Général sur la Protection des Données (RGPD) 
                entré en vigueur le 25 mai 2018 et à la loi Informatique et Libertés du 6 janvier 1978 modifiée.
              </p>
              <p>
                En utilisant notre site, vous acceptez les termes de cette politique de confidentialité.
              </p>
            </div>
          </section>

          {/* Article 1 - Responsable du traitement */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Responsable du traitement des données</h2>
            <div className="space-y-2 text-gray-700">
              <p><strong>Nom :</strong> ZEMMOUR Salim</p>
              <p><strong>Nom commercial :</strong> The Myli Boat</p>
              <p><strong>SIRET :</strong> 850 462 664 00036</p>
              <p><strong>Adresse :</strong> 40 Avenue de Hambourg, 13008 Marseille, France</p>
              <p><strong>Email :</strong> <a href="mailto:TheMyliBoat@gmail.com" className="text-blue-600 hover:underline">TheMyliBoat@gmail.com</a></p>
              <p><strong>Téléphone :</strong> <a href="tel:0644731667" className="text-blue-600 hover:underline">06 44 73 16 67</a></p>
            </div>
          </section>

          {/* Article 2 - Données collectées */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Données personnelles collectées</h2>
            <div className="space-y-3 text-gray-700">
              <p>Nous collectons les données suivantes lorsque vous effectuez une réservation :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Identité :</strong> Nom, prénom</li>
                <li><strong>Coordonnées :</strong> Adresse email, numéro de téléphone</li>
                <li><strong>Réservation :</strong> Date, créneau horaire, options choisies (assurance météo)</li>
                <li><strong>Paiement :</strong> Informations de transaction (montant, date) via notre prestataire Stripe (les données bancaires ne sont jamais stockées sur nos serveurs)</li>
              </ul>
              <p className="mt-4">
                <strong>Données de navigation :</strong><br />
                Lors de votre visite sur notre site, nous collectons automatiquement certaines informations 
                techniques (adresse IP, type de navigateur, pages consultées) à des fins d'analyse et d'amélioration 
                de nos services.
              </p>
            </div>
          </section>

          {/* Article 3 - Finalités */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Finalités du traitement</h2>
            <div className="space-y-3 text-gray-700">
              <p>Vos données personnelles sont collectées et traitées pour les finalités suivantes :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Gestion des réservations :</strong> Traitement et confirmation de votre réservation</li>
                <li><strong>Communication :</strong> Envoi d'emails de confirmation, rappels, et informations liées à votre sortie</li>
                <li><strong>Paiement :</strong> Traitement sécurisé du paiement de l'acompte via Stripe</li>
                <li><strong>Obligations légales :</strong> Respect des obligations comptables et fiscales</li>
                <li><strong>Amélioration des services :</strong> Analyse statistique de l'utilisation du site pour améliorer l'expérience utilisateur</li>
              </ul>
            </div>
          </section>

          {/* Article 4 - Base légale */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Base légale du traitement</h2>
            <div className="space-y-3 text-gray-700">
              <p>Le traitement de vos données personnelles repose sur les bases légales suivantes :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Exécution du contrat :</strong> Le traitement est nécessaire à l'exécution du contrat de location (réservation et prestation)</li>
                <li><strong>Obligation légale :</strong> Conservation des données pour répondre aux obligations comptables et fiscales</li>
                <li><strong>Consentement :</strong> Pour l'envoi d'informations marketing (si vous y avez consenti)</li>
              </ul>
            </div>
          </section>

          {/* Article 5 - Destinataires */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Destinataires des données</h2>
            <div className="space-y-3 text-gray-700">
              <p>Vos données personnelles sont accessibles uniquement aux personnes suivantes :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>The Myli Boat :</strong> Pour la gestion des réservations et la communication avec les clients</li>
                <li><strong>Stripe :</strong> Notre prestataire de paiement sécurisé (certifié PCI-DSS niveau 1)</li>
                <li><strong>Emergent :</strong> Notre hébergeur de site web</li>
              </ul>
              <p className="mt-4">
                Nous ne vendons, ne louons, ni ne partageons vos données avec des tiers à des fins commerciales.
              </p>
            </div>
          </section>

          {/* Article 6 - Durée de conservation */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Durée de conservation des données</h2>
            <div className="space-y-3 text-gray-700">
              <p>Vos données personnelles sont conservées pour les durées suivantes :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Données de réservation :</strong> 3 ans à compter de la dernière transaction (conformément aux obligations comptables)</li>
                <li><strong>Données de paiement :</strong> Les informations bancaires ne sont jamais stockées sur nos serveurs. Elles sont traitées directement par Stripe.</li>
                <li><strong>Cookies et données de navigation :</strong> 13 mois maximum</li>
              </ul>
              <p className="mt-4">
                À l'issue de ces délais, vos données sont supprimées de manière sécurisée.
              </p>
            </div>
          </section>

          {/* Article 7 - Droits des personnes */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Vos droits</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                Conformément au RGPD et à la loi Informatique et Libertés, vous disposez des droits suivants 
                concernant vos données personnelles :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Droit d'accès :</strong> Vous pouvez demander à consulter les données que nous détenons sur vous</li>
                <li><strong>Droit de rectification :</strong> Vous pouvez demander la correction de données inexactes</li>
                <li><strong>Droit à l'effacement :</strong> Vous pouvez demander la suppression de vos données (sous réserve des obligations légales de conservation)</li>
                <li><strong>Droit d'opposition :</strong> Vous pouvez vous opposer au traitement de vos données à des fins de prospection</li>
                <li><strong>Droit à la limitation :</strong> Vous pouvez demander la limitation du traitement de vos données</li>
                <li><strong>Droit à la portabilité :</strong> Vous pouvez recevoir vos données dans un format structuré et lisible</li>
              </ul>
              <p className="mt-4">
                <strong>Comment exercer vos droits ?</strong><br />
                Pour exercer l'un de ces droits, contactez-nous par email à l'adresse : 
                <a href="mailto:TheMyliBoat@gmail.com" className="text-blue-600 hover:underline"> TheMyliBoat@gmail.com</a>
              </p>
              <p>
                Nous nous engageons à répondre à votre demande dans un délai d'<strong>un mois</strong>.
              </p>
              <p className="mt-4">
                <strong>Droit de réclamation :</strong><br />
                Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation 
                auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) : 
                <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline"> www.cnil.fr</a>
              </p>
            </div>
          </section>

          {/* Article 8 - Sécurité */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Sécurité des données</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                Nous mettons en œuvre toutes les mesures techniques et organisationnelles appropriées pour 
                protéger vos données personnelles contre la perte, l'utilisation abusive, l'accès non autorisé, 
                la divulgation, la modification ou la destruction.
              </p>
              <p>
                <strong>Mesures de sécurité :</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Connexion HTTPS sécurisée (cryptage SSL/TLS)</li>
                <li>Paiements sécurisés via Stripe (certifié PCI-DSS niveau 1)</li>
                <li>Hébergement sécurisé</li>
                <li>Accès limité aux données personnelles uniquement aux personnes habilitées</li>
              </ul>
            </div>
          </section>

          {/* Article 9 - Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Cookies</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                <strong>Qu'est-ce qu'un cookie ?</strong><br />
                Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, smartphone, tablette) 
                lors de la consultation de notre site.
              </p>
              <p>
                <strong>Cookies utilisés sur notre site :</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Cookies strictement nécessaires :</strong> Essentiels au fonctionnement du site (gestion du panier, session de paiement)</li>
                <li><strong>Cookies analytiques :</strong> Nous permettent de mesurer l'audience et d'améliorer l'expérience utilisateur</li>
              </ul>
              <p className="mt-4">
                <strong>Gestion des cookies :</strong><br />
                Vous pouvez à tout moment configurer votre navigateur pour refuser les cookies. 
                Cependant, cela peut limiter certaines fonctionnalités du site (notamment le processus de réservation).
              </p>
            </div>
          </section>

          {/* Article 10 - Transferts de données */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Transferts de données hors UE</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                Vos données personnelles peuvent être transférées et traitées en dehors de l'Union Européenne 
                dans le cadre de l'utilisation de nos prestataires (notamment Stripe).
              </p>
              <p>
                Ces transferts sont encadrés par des garanties appropriées conformément au RGPD 
                (clauses contractuelles types, Privacy Shield, etc.).
              </p>
            </div>
          </section>

          {/* Article 11 - Modifications */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Modifications de la politique de confidentialité</h2>
            <p className="text-gray-700">
              Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. 
              Toute modification sera publiée sur cette page avec indication de la date de mise à jour.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-blue-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact</h2>
            <div className="space-y-2 text-gray-700">
              <p>Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits :</p>
              <p><strong>Email :</strong> <a href="mailto:TheMyliBoat@gmail.com" className="text-blue-600 hover:underline">TheMyliBoat@gmail.com</a></p>
              <p><strong>Téléphone :</strong> <a href="tel:0644731667" className="text-blue-600 hover:underline">06 44 73 16 67</a></p>
              <p><strong>Adresse :</strong> 40 Avenue de Hambourg, 13008 Marseille, France</p>
            </div>
          </section>

          {/* Date de mise à jour */}
          <section className="pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              <strong>Dernière mise à jour :</strong> {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </section>

        </div>

        {/* Liens vers autres pages légales */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/mentions-legales')}
            className="w-full py-6"
          >
            ⚖️ Mentions Légales
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/cgv')}
            className="w-full py-6"
          >
            📄 Conditions Générales de Vente
          </Button>
        </div>

        {/* Retour accueil */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900"
          >
            Retour à l'accueil
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PolitiqueConfidentialite;
